import { Autocomplete, Button, ButtonVariantEnum, Checkbox, Modal, TextInput } from '@Wonder-Cave/ui';
import { SystemNumberStatusEnum } from '@shared/enums/system-number-status-enum';
import { ISearchTcrCampaignsRecord } from '@shared/models';
import { Formik } from 'formik';
import { useMemo } from 'react';
import * as yup from 'yup';
import { IClientLocalSystemNumberRecord } from '../../../../hooks/useClientLocalSystemNumbers';
import { IDropdownValue } from '../../../shared/Form/Dropdown';
import { getAreaCode, normalizePhoneDigits, uniqueSorted } from './number-management.helpers';

export interface IBulkDeactivateForm {
  selectedProviderNames: string[];
  selectedTcrCampaignIds: string[];
  areaCode: string;
  qty: number;
}

export type BulkDeactivateExclusionReason =
  | 'PROVIDER_NOT_SELECTED'
  | 'AREA_CODE_NOT_SELECTED'
  | 'MISSING_GR_TCR_CAMPAIGN';

export interface IBulkDeactivateEligiblePhoneNumber {
  phoneNumber: string;
  providerName: string | null;
  exclusionReason: BulkDeactivateExclusionReason | null;
}

export interface IBulkDeactivateEligibleCampaign {
  grTcrCampaignId: string;
  tcrCampaignLabel: string;
  matchingCount: number;
  phoneNumbers: IBulkDeactivateEligiblePhoneNumber[];
}

export interface IBulkDeactivateIneligibleCampaign {
  tcrCampaignLabel: string;
  matchingCount: number;
}

export interface IBulkDeactivateReviewSummary {
  qty: number;
  selectedTcrCount: number | null;
  selectedAreaCode: string;
  eligibleCampaigns: IBulkDeactivateEligibleCampaign[];
  ineligibleCampaigns: IBulkDeactivateIneligibleCampaign[];
}

interface IDeactivateNumbersBulkModalProps {
  show: boolean;
  records: IClientLocalSystemNumberRecord[];
  allTcrCampaigns: ISearchTcrCampaignsRecord[];
  tcrCampaignsLoading: boolean;
  onClose: () => void;
  onSubmit: (summary: IBulkDeactivateReviewSummary) => void;
}

const bulkDeactivateSchema = yup.object().shape({
  selectedProviderNames: yup.array().of(yup.string().required()).default([]),
  selectedTcrCampaignIds: yup.array().of(yup.string().required()).default([]),
  areaCode: yup.string().default(''),
  qty: yup.number().integer('Must be a whole number').min(1, 'Must be >= 1').required('Quantity is required'),
});

const isActiveForDeactivation = (status: SystemNumberStatusEnum): boolean =>
  status !== SystemNumberStatusEnum.DEACTIVATING && status !== SystemNumberStatusEnum.DEACTIVATED;

const tcrCampaignIdsForProviders = (
  records: IClientLocalSystemNumberRecord[],
  selectedProviderNames: string[],
): string[] => {
  if (selectedProviderNames.length === 0) {
    return uniqueSorted(records.map(r => r.tcrCampaignId));
  }
  const providerSet = new Set(selectedProviderNames);
  return uniqueSorted(
    records
      .filter(r => !!r.providerName && providerSet.has(r.providerName))
      .map(r => r.tcrCampaignId),
  );
};

const buildReviewSummary = (
  records: IClientLocalSystemNumberRecord[],
  form: IBulkDeactivateForm,
  allTcrCampaigns: ISearchTcrCampaignsRecord[],
): IBulkDeactivateReviewSummary => {
  const buildCampaignLabel = (tcrCampaignId: string): string => {
    const campaign = allTcrCampaigns.find(c => c.tcrCampaignId === tcrCampaignId);
    if (!campaign) return tcrCampaignId;
    const parts: string[] = [tcrCampaignId];
    if (campaign.provider?.name) parts.push(campaign.provider.name);
    if (campaign.messageType) parts.push(campaign.messageType);
    return parts.join(' - ');
  };
  const providerFilterActive = form.selectedProviderNames.length > 0;
  const providerSet = new Set(form.selectedProviderNames);
  const tcrFilterActive = form.selectedTcrCampaignIds.length > 0;
  const tcrSet = new Set(form.selectedTcrCampaignIds);
  const areaCodeFilter = form.areaCode.trim();
  const areaCodeFilterActive = areaCodeFilter.length > 0;

  const activeRecords = records.filter(r =>
    isActiveForDeactivation(r.status)
    && !!r.providerName
    && !!r.tcrCampaignId
  );

  const byTcr = new Map<string, IClientLocalSystemNumberRecord[]>();
  for (const r of activeRecords) {
    const tcrId = r.tcrCampaignId!;
    const list = byTcr.get(tcrId) ?? [];
    list.push(r);
    byTcr.set(tcrId, list);
  }

  const eligible: IBulkDeactivateEligibleCampaign[] = [];
  const ineligible: IBulkDeactivateIneligibleCampaign[] = [];

  for (const [tcrCampaignId, recs] of byTcr) {
    const tcrIncluded = !tcrFilterActive || tcrSet.has(tcrCampaignId);
    if (!tcrIncluded) continue;

    // When provider filter is active but the user made no explicit TCR selection, skip
    // campaigns that have no records for the selected provider — they only add noise.
    if (providerFilterActive && !tcrFilterActive && !recs.some(r => providerSet.has(r.providerName!))) {
      continue;
    }

    const grTcrCampaignId = recs.find(r => !!r.grTcrCampaignId)?.grTcrCampaignId ?? '';
    const missingGrTcr = !grTcrCampaignId;

    const categorized = recs.map(r => {
      const reason: BulkDeactivateExclusionReason | null = missingGrTcr
        ? 'MISSING_GR_TCR_CAMPAIGN'
        : providerFilterActive && !providerSet.has(r.providerName!)
          ? 'PROVIDER_NOT_SELECTED'
          : areaCodeFilterActive && getAreaCode(r.phoneNumber) !== areaCodeFilter
            ? 'AREA_CODE_NOT_SELECTED'
            : null;
      return { record: r, reason };
    });

    const included = categorized
      .filter(x => x.reason === null)
      .slice(0, form.qty)
      .map<IBulkDeactivateEligiblePhoneNumber>(x => ({
        phoneNumber: normalizePhoneDigits(x.record.phoneNumber),
        providerName: x.record.providerName,
        exclusionReason: null,
      }));

    const excluded = categorized
      .filter(x => x.reason !== null)
      .map<IBulkDeactivateEligiblePhoneNumber>(x => ({
        phoneNumber: normalizePhoneDigits(x.record.phoneNumber),
        providerName: x.record.providerName,
        exclusionReason: x.reason,
      }));

    if (included.length === 0 && excluded.length === 0) {
      continue;
    }

    eligible.push({
      grTcrCampaignId,
      tcrCampaignLabel: buildCampaignLabel(tcrCampaignId),
      matchingCount: recs.length,
      phoneNumbers: [...included, ...excluded],
    });
  }

  // Selected campaigns with no active numbers at all won't appear in byTcr.
  // Add them explicitly so the review modal can synthesize zero-count rows.
  if (tcrFilterActive) {
    for (const tcrCampaignId of tcrSet) {
      if (!byTcr.has(tcrCampaignId)) {
        eligible.push({
          grTcrCampaignId: '',
          tcrCampaignLabel: buildCampaignLabel(tcrCampaignId),
          matchingCount: 0,
          phoneNumbers: [],
        });
      }
    }
  }

  return {
    qty: form.qty,
    selectedTcrCount: tcrFilterActive ? tcrSet.size : null,
    selectedAreaCode: areaCodeFilter,
    eligibleCampaigns: eligible,
    ineligibleCampaigns: ineligible,
  };
};

const DeactivateNumbersBulkModal = ({
  show,
  records,
  allTcrCampaigns,
  tcrCampaignsLoading,
  onClose,
  onSubmit,
}: IDeactivateNumbersBulkModalProps) => {
  const allProviderNames = useMemo(() => uniqueSorted(records.map(r => r.providerName)), [records]);

  const activeCampaignIds = useMemo(
    () => new Set(allTcrCampaigns.map(c => c.tcrCampaignId).filter((id): id is string => !!id)),
    [allTcrCampaigns],
  );

  const campaignLabel = (tcrCampaignId: string): string => {
    const campaign = allTcrCampaigns.find(c => c.tcrCampaignId === tcrCampaignId);
    if (!campaign) return tcrCampaignId;
    const parts: string[] = [tcrCampaignId];
    if (campaign.provider?.name) parts.push(campaign.provider.name);
    if (campaign.messageType) parts.push(campaign.messageType);
    return parts.join(' - ');
  };

  const initialValues = useMemo<IBulkDeactivateForm>(() => ({
    selectedProviderNames: [],
    selectedTcrCampaignIds: [],
    areaCode: '',
    qty: 49,
  }), []);

  const titleComponent = <div className='flex flex-col mt-4 mb-4'>
    <h2 className='w-full'>Deactivate Numbers</h2>
    <div className='pt-2 text-regular font-medium text-dark-gray'>Select numbers to deactivate</div>
  </div>;

  return (
    <Modal
      title={titleComponent}
      show={show}
      onClose={onClose}
      titleClassName='pl-20 text-black'
      childrenClassName='modal-panel-grey pl-20 pr-20 pb-2 pt-2'
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={bulkDeactivateSchema}
        validateOnBlur
        validateOnChange
        onSubmit={(values: IBulkDeactivateForm) => onSubmit(buildReviewSummary(records, values, allTcrCampaigns))}
      >
        {({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched, isValid }) => {
          const tcrCampaignIds = tcrCampaignIdsForProviders(records, values.selectedProviderNames)
            .filter(id => activeCampaignIds.has(id));

          // Area codes filtered to selected TCR campaigns, then selected providers, then all
          const areaCodeFilteredRecords = (() => {
            if (values.selectedTcrCampaignIds.length > 0) {
              const tcrSet = new Set(values.selectedTcrCampaignIds);
              return records.filter(r => r.tcrCampaignId && tcrSet.has(r.tcrCampaignId));
            }
            if (values.selectedProviderNames.length > 0) {
              const providerSet = new Set(values.selectedProviderNames);
              return records.filter(r => r.providerName && providerSet.has(r.providerName));
            }
            return records;
          })();

          const areaCodeOptions: IDropdownValue[] = uniqueSorted(
            areaCodeFilteredRecords.map(r => getAreaCode(r.phoneNumber)),
          ).map(code => ({ label: code, value: code }));

          const pruneAreaCodeForSelection = (nextAreaCodeOptions: IDropdownValue[]) => {
            if (values.areaCode && !nextAreaCodeOptions.some(o => o.value === values.areaCode)) {
              setFieldValue('areaCode', '');
            }
          };

          const tcrCampaignOptions: IDropdownValue[] = tcrCampaignIds.map(id => ({
            label: campaignLabel(id),
            value: id,
          }));
          const providerOptions: IDropdownValue[] = allProviderNames.map(name => ({ label: name, value: name }));

          const selectedProviderDropdownValues: IDropdownValue[] = values.selectedProviderNames.map(name => ({ label: name, value: name }));
          const selectedTcrCampaignDropdownValues: IDropdownValue[] = values.selectedTcrCampaignIds.map(id => ({
            label: campaignLabel(id),
            value: id,
          }));
          const selectedAreaCodeDropdownValue: IDropdownValue | undefined = values.areaCode
            ? { label: values.areaCode, value: values.areaCode }
            : undefined;

          const pruneTcrSelectionsForProviders = (nextProviders: string[]) => {
            const validIds = new Set(
              tcrCampaignIdsForProviders(records, nextProviders).filter(id => activeCampaignIds.has(id)),
            );
            const pruned = values.selectedTcrCampaignIds.filter(id => validIds.has(id));
            if (pruned.length !== values.selectedTcrCampaignIds.length) {
              setFieldValue('selectedTcrCampaignIds', pruned);
            }
          };

          const allProvidersSelected = allProviderNames.length > 0
            && values.selectedProviderNames.length === allProviderNames.length;
          const allTcrCampaignsSelected = tcrCampaignIds.length > 0
            && values.selectedTcrCampaignIds.length === tcrCampaignIds.length;

          const toggleSelectAllProviders = () => {
            setFieldTouched('selectedProviderNames');
            const next = allProvidersSelected ? [] : allProviderNames;
            setFieldValue('selectedProviderNames', next);
            pruneTcrSelectionsForProviders(next);
            const nextValidTcrIds = new Set(
              tcrCampaignIdsForProviders(records, next).filter(id => activeCampaignIds.has(id)),
            );
            const prunedTcrSelections = values.selectedTcrCampaignIds.filter(id => nextValidTcrIds.has(id));
            const nextAreaCodes = (() => {
              if (prunedTcrSelections.length > 0) {
                const tSet = new Set(prunedTcrSelections);
                return records.filter(r => r.tcrCampaignId && tSet.has(r.tcrCampaignId));
              }
              if (next.length > 0) {
                const pSet = new Set(next);
                return records.filter(r => r.providerName && pSet.has(r.providerName));
              }
              return records;
            })();
            pruneAreaCodeForSelection(
              uniqueSorted(nextAreaCodes.map(r => getAreaCode(r.phoneNumber))).map(code => ({ label: code, value: code })),
            );
          };

          const toggleSelectAllTcrCampaigns = () => {
            setFieldTouched('selectedTcrCampaignIds');
            const next = allTcrCampaignsSelected ? [] : tcrCampaignIds;
            setFieldValue('selectedTcrCampaignIds', next);
            const nextAreaCodes = (() => {
              if (next.length > 0) {
                const tSet = new Set(next);
                return records.filter(r => r.tcrCampaignId && tSet.has(r.tcrCampaignId));
              }
              if (values.selectedProviderNames.length > 0) {
                const pSet = new Set(values.selectedProviderNames);
                return records.filter(r => r.providerName && pSet.has(r.providerName));
              }
              return records;
            })();
            pruneAreaCodeForSelection(
              uniqueSorted(nextAreaCodes.map(r => getAreaCode(r.phoneNumber))).map(code => ({ label: code, value: code })),
            );
          };

          const submitDisabled = !isValid;

          return (
            <form onSubmit={handleSubmit} className='flex flex-col w-full'>
              <div className='flex flex-col pb-6'>
                <div className='flex flex-row items-center justify-between pb-2 [&_h4]:font-medium'>
                  <h5 className='text-medium-gray'>PROVIDER</h5>
                  <Checkbox
                    id='select-all-providers'
                    name='select-all-providers'
                    label='Select All'
                    checked={allProvidersSelected}
                    disabled={tcrCampaignsLoading || allProviderNames.length === 0}
                    onChange={toggleSelectAllProviders}
                  />
                </div>
                <Autocomplete
                  label=''
                  placeholder={tcrCampaignsLoading ? 'Loading providers...' : allProviderNames.length === 0 ? 'No providers available' : 'Type to search providers'}
                  value={selectedProviderDropdownValues}
                  options={providerOptions}
                  multiple
                  disabled={tcrCampaignsLoading}
                  showError={!!(touched.selectedProviderNames && errors.selectedProviderNames)}
                  error={touched.selectedProviderNames ? (errors.selectedProviderNames as string) : ''}
                  onChange={(newValue?: IDropdownValue[]) => {
                    setFieldTouched('selectedProviderNames');
                    const next = (newValue ?? []).map(o => o?.value).filter((v): v is string => !!v);
                    setFieldValue('selectedProviderNames', next);
                    pruneTcrSelectionsForProviders(next);
                    // Compute area codes for the incoming provider selection + surviving TCR selections
                    const nextValidTcrIds = new Set(
                      tcrCampaignIdsForProviders(records, next).filter(id => activeCampaignIds.has(id)),
                    );
                    const prunedTcrSelections = values.selectedTcrCampaignIds.filter(id => nextValidTcrIds.has(id));
                    const nextAreaCodes = (() => {
                      if (prunedTcrSelections.length > 0) {
                        const tSet = new Set(prunedTcrSelections);
                        return records.filter(r => r.tcrCampaignId && tSet.has(r.tcrCampaignId));
                      }
                      if (next.length > 0) {
                        const pSet = new Set(next);
                        return records.filter(r => r.providerName && pSet.has(r.providerName));
                      }
                      return records;
                    })();
                    pruneAreaCodeForSelection(
                      uniqueSorted(nextAreaCodes.map(r => getAreaCode(r.phoneNumber))).map(code => ({ label: code, value: code })),
                    );
                  }}
                  onBlur={() => setFieldTouched('selectedProviderNames')}
                />
              </div>

              <div className='flex flex-col pb-6'>
                <div className='flex flex-row items-center justify-between pb-2 [&_h4]:font-medium'>
                  <h5 className='text-medium-gray'>SELECT TCR CAMPAIGNS</h5>
                  <Checkbox
                    id='select-all-tcr'
                    name='select-all-tcr'
                    label='Select All'
                    checked={allTcrCampaignsSelected}
                    disabled={tcrCampaignsLoading || tcrCampaignIds.length === 0}
                    onChange={toggleSelectAllTcrCampaigns}
                  />
                </div>
                <Autocomplete
                  label=''
                  placeholder={tcrCampaignsLoading ? 'Loading campaigns...' : tcrCampaignIds.length === 0 ? 'No active campaigns available' : 'Type to search TCR campaigns'}
                  value={selectedTcrCampaignDropdownValues}
                  options={tcrCampaignOptions}
                  multiple
                  disabled={tcrCampaignsLoading}
                  showError={!!(touched.selectedTcrCampaignIds && errors.selectedTcrCampaignIds)}
                  error={touched.selectedTcrCampaignIds ? (errors.selectedTcrCampaignIds as string) : ''}
                  onChange={(newValue?: IDropdownValue[]) => {
                    setFieldTouched('selectedTcrCampaignIds');
                    const next = (newValue ?? []).map(o => o?.value).filter((v): v is string => !!v);
                    setFieldValue('selectedTcrCampaignIds', next);
                    const nextAreaCodes = (() => {
                      if (next.length > 0) {
                        const tSet = new Set(next);
                        return records.filter(r => r.tcrCampaignId && tSet.has(r.tcrCampaignId));
                      }
                      if (values.selectedProviderNames.length > 0) {
                        const pSet = new Set(values.selectedProviderNames);
                        return records.filter(r => r.providerName && pSet.has(r.providerName));
                      }
                      return records;
                    })();
                    pruneAreaCodeForSelection(
                      uniqueSorted(nextAreaCodes.map(r => getAreaCode(r.phoneNumber))).map(code => ({ label: code, value: code })),
                    );
                  }}
                  onBlur={() => setFieldTouched('selectedTcrCampaignIds')}
                />
              </div>

              <div className='grid grid-cols-2 gap-x-8 pb-6'>
                <div className='flex flex-col'>
                  <h5 className='pb-2 text-medium-gray'>AREA CODE</h5>
                  <Autocomplete
                    label=''
                    placeholder={areaCodeOptions.length === 0 ? 'No area codes available' : 'Search area codes'}
                    value={selectedAreaCodeDropdownValue}
                    options={areaCodeOptions}
                    showError={!!(touched.areaCode && errors.areaCode)}
                    error={touched.areaCode ? errors.areaCode : ''}
                    onChange={(newValue?: IDropdownValue) => {
                      setFieldTouched('areaCode');
                      setFieldValue('areaCode', newValue?.value ?? '');
                    }}
                    onBlur={() => setFieldTouched('areaCode')}
                  />
                </div>

                <div className='flex flex-col'>
                  <h5 className='pb-2 text-medium-gray'>QUANTITY</h5>
                  <TextInput
                    id='qty'
                    name='qty'
                    type='number'
                    placeholder='Quantity'
                    value={values.qty as any}
                    error={touched.qty ? errors.qty : ''}
                    onBlur={() => setFieldTouched('qty')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldTouched('qty');
                      const parsed = Number(event.target.value);
                      setFieldValue('qty', Number.isFinite(parsed) ? parsed : 0);
                    }}
                  />
                </div>
              </div>

              <div className='flex flex-row justify-end pb-4'>
                <Button variant={ButtonVariantEnum.TERTIARY} type='submit' disabled={submitDisabled} className='!bg-fire'>
                  DEACTIVATE NUMBERS
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default DeactivateNumbersBulkModal;
